import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Select,
  Input,
  VStack,
  HStack,
  Spacer,
} from "@chakra-ui/react";
import DataTable from "../AppDataTable";
import { tableCustomStyles } from "../../themes/TableStyles";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import {
  Search,
  ExportExcel,
  ExportPdf,
  Print,
  Clear,
} from "../../themes/IconButtonSet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTable } from "@fortawesome/free-solid-svg-icons";
import CustomDatePicker from "../../components/CustomDatePicker";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

// ฟังก์ชันโหลดฟอนต์ Sarabun
const loadSarabunFont = async () => {
  try {
    const fontResponse = await fetch("/fonts/Sarabun-Regular.ttf");
    if (!fontResponse.ok) {
      throw new Error(
        `Failed to fetch Sarabun font: ${fontResponse.statusText}`
      );
    }
    const fontArrayBuffer = await fontResponse.arrayBuffer();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result
          .replace("data:application/octet-stream;base64,", "")
          .replace("data:font/ttf;base64,", "");
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(new Blob([fontArrayBuffer]));
    });
  } catch (error) {
    console.error("Error loading Sarabun font:", error);
    return null;
  }
};

const VaccineQCResultReport = () => {
  const { sessionId } = useAuth();
  const today = new Date();
  const [filterData, setFilterData] = useState({
    VaccineNameTH: "",
    LotID: "",
    StartDate: null,
    EndDate: null,
    QCResultPassed: "",
  });
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [lotOptions, setLotOptions] = useState([]);
  const [testingData, setTestingData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const minDateForEndDate = filterData.StartDate
    ? new Date(filterData.StartDate)
    : undefined;

  // ดึงข้อมูล Vaccine List
  const fetchVaccineOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineOptions(response.VaccinesList || []);
      } else {
        console.error("Failed to fetch vaccines:", response.error);
      }
    } catch (error) {
      console.error("Error fetching vaccines:", error);
    }
  };

  // ดึงข้อมูล Lot List
  const fetchLotOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineLotList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setLotOptions(response.LotList || []);
      } else {
        console.error("Failed to fetch lots:", response.error);
      }
    } catch (error) {
      console.error("Error fetching lots:", error);
    }
  };

  // เรียก API getVaccineQCResultSummary
  const fetchTestingDeliveryReport = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineQCResultSummary",
        data: {
          VaccineNameTH: filterData.VaccineNameTH || "",
          LotID: filterData.LotID || "",
          StartDate: filterData.StartDate || "",
          EndDate: filterData.EndDate || "",
          QCResultPassed:
            filterData.QCResultPassed === ""
              ? ""
              : filterData.QCResultPassed === "1"
              ? 1
              : 0,
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setTestingData(response.summary || []);
      } else {
        setTestingData([]);
        alert("ไม่พบข้อมูล: " + response.error);
      }
    } catch (error) {
      console.error("Error fetching testing delivery report:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchVaccineOptions();
      fetchLotOptions();
    }
  }, [sessionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData({ ...filterData, [name]: value });
  };

  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>วัคซีน (TH)</span>
        </HStack>
      ),
      selector: (row) => row.VaccineNameTH,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>วัคซีน (EN)</span>
        </HStack>
      ),
      selector: (row) => row.VaccineNameEN,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>Lot No.</span>
        </HStack>
      ),
      selector: (row) => row.LotID,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนที่ผลิต (ขวด)</span>
        </HStack>
      ),
      selector: (row) => row.RequestQuantity,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>วันที่ผลิต</span>
        </HStack>
      ),
      selector: (row) => row.ProductionDate,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>วันหมดอายุ</span>
        </HStack>
      ),
      selector: (row) => row.ExpiryDate,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ผลการทดสอบ</span>
        </HStack>
      ),
      selector: (row) => (row.QCResultPassed ? "ผ่าน" : "ไม่ผ่าน"),
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนที่ส่งทดสอบ (ขวด)</span>
        </HStack>
      ),
      selector: (row) => row.QCQuantity,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนที่ทดสอบผ่าน (ขวด)</span>
        </HStack>
      ),
      selector: (row) => row.QCAmountPassed,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนที่ทดสอบไม่ผ่าน (ขวด)</span>
        </HStack>
      ),
      selector: (row) => row.QCAmountFailed,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>หมายเหตุ</span>
        </HStack>
      ),
      selector: (row) => row.QCResultRemark,
      sortable: true,
    },
  ];

  const exportToExcel = () => {
    const wsData = testingData.map((item) => ({
      "วัคซีน (TH)": item.VaccineNameTH,
      "วัคซีน (EN)": item.VaccineNameEN,
      "Lot No.": item.LotID,
      "จำนวนที่ผลิต (ขวด)": item.RequestQuantity,
      วันที่ผลิต: item.ProductionDate,
      วันหมดอายุ: item.ExpiryDate,
      ผลการทดสอบ: item.QCResultPassed ? "ผ่าน" : "ไม่ผ่าน",
      "จำนวนที่ส่งทดสอบ (ขวด)": item.QCQuantity,
      "จำนวนที่ทดสอบผ่าน (ขวด)": item.QCAmountPassed,
      "จำนวนที่ทดสอบไม่ผ่าน (ขวด)": item.QCAmountFailed,
      หมายเหตุ: item.QCResultRemark,
    }));
    const ws = XLSX.utils.json_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "TestingDeliveryReport");
    XLSX.writeFile(wb, "VaccineQCResultReport.xlsx");
  };

  const exportToPDF = async () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const fontBase64 = await loadSarabunFont();
    if (fontBase64) {
      doc.addFileToVFS("Sarabun.ttf", fontBase64);
      doc.addFont("Sarabun.ttf", "Sarabun", "normal");
      doc.setFont("Sarabun");
    } else {
      console.warn("Failed to load Sarabun font, using default font");
    }

    doc.setFontSize(16);
    doc.text("รายงานผลการส่งวัคซีนทดสอบ", 14, 10);
    doc.setFontSize(10);
    doc.text(`วันที่: ${new Date().toLocaleDateString("th-TH")}`, 14, 18);

    autoTable(doc, {
      startY: 25,
      head: [
        [
          "วัคซีน (TH)",
          "วัคซีน (EN)",
          "Lot No.",
          "จำนวนที่ผลิต (ขวด)",
          "วันที่ผลิต",
          "วันหมดอายุ",
          "ผลการทดสอบ",
          "จำนวนที่ส่งทดสอบ (ขวด)",
          "จำนวนที่ทดสอบผ่าน (ขวด)",
          "จำนวนที่ทดสอบไม่ผ่าน (ขวด)",
          "หมายเหตุ",
        ],
      ],
      body: testingData.map((item) => [
        item.VaccineNameTH,
        item.VaccineNameEN,
        item.LotID,
        item.RequestQuantity,
        item.ProductionDate,
        item.ExpiryDate,
        item.QCResultPassed ? "ผ่าน" : "ไม่ผ่าน",
        item.QCQuantity,
        item.QCAmountPassed,
        item.QCAmountFailed,
        item.QCResultRemark,
      ]),
      styles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontSize: 12,
        cellPadding: 2,
      },
      headStyles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontStyle: "normal",
        fillColor: [22, 160, 133],
        textColor: [255, 255, 255],
      },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 40 },
        2: { cellWidth: 30 },
        3: { cellWidth: 30 },
        4: { cellWidth: 30 },
        5: { cellWidth: 30 },
        6: { cellWidth: 20 },
        7: { cellWidth: 30 },
        8: { cellWidth: 50 },
      },
      didParseCell: (data) => {
        data.cell.styles.cellWidth = "auto";
      },
    });

    doc.save("VaccineQCResultReport.pdf");
  };

  const printTable = () => {
    const printContent = `
      <html>
        <head>
          <title>Vaccine Testing Delivery Report</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <h2>Vaccine Testing Delivery Report</h2>
          <table>
            <thead>
              <tr>
                <th>วัคซีน (TH)</th>
                <th>วัคซีน (EN)</th>
                <th>Lot No.</th>
                <th>จำนวนที่ผลิต (ขวด)</th>
                <th>วันที่ผลิต</th>
                <th>วันหมดอายุ</th>
                <th>ผลการทดสอบ</th>
                <th>จำนวนที่ส่งทดสอบ (ขวด)</th>
                <th>จำนวนที่ทดสอบผ่าน (ขวด)</th>
                <th>จำนวนที่ทดสอบไม่ผ่าน (ขวด)</th>
                <th>หมายเหตุ</th>
              </tr>
            </thead>
            <tbody>
              ${testingData
                .map(
                  (item) => `
                    <tr>
                      <td>${item.VaccineNameTH}</td>
                      <td>${item.VaccineNameEN}</td>
                      <td>${item.LotID}</td>
                      <td>${item.RequestQuantity}</td>
                      <td>${item.ProductionDate}</td>
                      <td>${item.ExpiryDate}</td>
                      <td>${item.QCResultPassed ? "ผ่าน" : "ไม่ผ่าน"}</td>
                      <td>${item.QCQuantity}</td>
                      <td>${item.QCAmountPassed}</td>
                      <td>${item.QCAmountFailed}</td>
                      <td>${item.QCResultRemark}</td>
                    </tr>
                  `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=800,height=600");
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

  return (
    <Box p={35} m={30} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <StandardPageTitle title="รายงานผลการส่งวัคซีนทดสอบ" />

      {/* ฟอร์มฟิลเตอร์ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mt={5} mb={6}>
        <StandardHeader
          title="ค้นหาข้อมูลผลการส่งวัคซีนทดสอบ"
          bgColor="iBlue.600"
          icon={faSearch}
        />
        <VStack spacing={6} p={5}>
          <HStack spacing={6} w="100%" flexWrap="wrap">
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วัคซีน
              </FormLabel>
              <Select
                name="VaccineNameTH"
                value={filterData.VaccineNameTH}
                onChange={handleInputChange}
                placeholder="เลือกวัคซีน (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {vaccineOptions.map((vaccine) => (
                  <option key={vaccine.VaccineID} value={vaccine.VaccineNameTH}>
                    {vaccine.VaccineNameTH}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                Lot No.
              </FormLabel>
              <Select
                name="LotID"
                value={filterData.LotID}
                onChange={handleInputChange}
                placeholder="เลือก Lot (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {lotOptions.map((lot) => (
                  <option key={lot.LotID} value={lot.LotID}>
                    {lot.LotID}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วันที่ผลิต (เริ่มต้น)
              </FormLabel>
              <CustomDatePicker
                name="StartDate"
                value={filterData.StartDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
                // --- 3. กำหนด maxDate ให้กับ "วันเริ่มต้น" ---
                maxDate={today}
              />
            </FormControl>

            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วันที่ผลิต (สิ้นสุด)
              </FormLabel>
              <CustomDatePicker
                name="EndDate"
                value={filterData.EndDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
                // --- 4. กำหนด minDate และ maxDate ให้กับ "วันสิ้นสุด" ---
                minDate={minDateForEndDate} // <--- ค่านี้จะเปลี่ยนไปตาม StartDate
                maxDate={today}
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ผลการทดสอบ
              </FormLabel>
              <Select
                name="QCResultPassed"
                value={filterData.QCResultPassed}
                onChange={handleInputChange}
                placeholder="เลือกผลการทดสอบ (ทั้งหมด)"
                colorScheme="iBlue"
              >
                <option value="">ทั้งหมด</option>
                <option value="1">ผ่าน</option>
                <option value="0">ไม่ผ่าน</option>
              </Select>
            </FormControl>
          </HStack>
          <Box w="100%" textAlign="right">
            <HStack spacing={5} justifyContent="flex-end">
              <Clear
                onClick={() =>
                  setFilterData({
                    VaccineNameTH: "",
                    LotID: "",
                    StartDate: "",
                    EndDate: "",
                    QCResultPassed: "",
                  })
                }
              />
              <Search
                onClick={fetchTestingDeliveryReport}
                isLoading={isLoading}
              />
            </HStack>
          </Box>
        </VStack>
      </Box>

      {/* ตาราง */}
      {testingData.length > 0 && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <StandardHeader
            title="รายการผลการส่งวัคซีนทดสอบ"
            bgColor="iOrange.500"
            icon={faTable}
          />
          <HStack spacing={5} m={5}>
            <Spacer />
            <ExportExcel onClick={exportToExcel} />
            <ExportPdf onClick={exportToPDF} />
            <Print onClick={printTable} />
          </HStack>
          <Box p={5} overflowX="auto">
            <DataTable
              columns={columns}
              data={testingData}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default VaccineQCResultReport;
